import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Lock, AlertCircle, ChevronRight, Navigation, Flag, CheckCircle, 
  MapPin, Info, Truck, X, Menu, User, Building2, Phone, DollarSign, 
  Calendar, Route, Weight, Ruler, Star, Clock, FileText, Car, Hash, RotateCcw 
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, selectUserType, logoutUser } from '../store/slices/authSlice';
import { selectLoadById } from '../store/slices/loadSlice';
import { selectCarrierLoadById } from '../store/slices/loadCarrierSlice';
import LoadStatusTimeline from '../components/LoadStatusTimeline';
import LoadTrackingMap from '../components/LoadTrackingMap';
import DocumentCard from '../components/DocumentCard';
import { fetchSingleLoad } from '../store/slices/loadSlice';
import { fetchSingleCarrierLoad } from '../store/slices/loadCarrierSlice';
import SideNavigation from '../components/SideNavigation';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { fetchTrackingHistory } from '../store/slices/trackingHistorySlice';
import useScreenSize from '../hooks/useScreenSize';
import Card from '../components/Card';
import SectionHeader from '../components/SectionHeader';
import DetailItem from '../components/DetailItem';
import VehicleDetailItem from '../components/VehicleDetailItem';
import { getLoadDisplayId, isLoadExpired } from '../utils/loadUtils';

// Helper function to safely format dates like LoadDetails.js does
const safeFormatDate = (date) => {
  if (!date) return null;
  
  try {
    // Extract just the date part from the ISO string to avoid timezone issues
    const datePart = date.includes('T') ? date.split('T')[0] : date;
    const dateObj = new Date(datePart + 'T00:00:00');
    
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date:', date);
      return null;
    }
    
    return dateObj.toLocaleDateString();
  } catch (error) {
    console.error('Date formatting error:', error, { date });
    return null;
  }
};

// Helper functions to get first pickup and last delivery from stops
const getFirstPickup = (load) => {
  if (!load.stops || load.stops.length === 0) {
    // Fallback to legacy pickup data
    return {
      date: load.pickup_date,
      time: load.pickup_time,
      city: load.origin_city,
      state: load.origin_state,
      address: load.origin_address
    };
  }
  
  // Find first pickup stop (lowest sequence with type 'pickup')
  const pickupStops = load.stops.filter(stop => stop.stop_type === 'pickup');
  if (pickupStops.length === 0) {
    // If no pickup stops found, use first stop
    const firstStop = load.stops.sort((a, b) => a.sequence - b.sequence)[0];
    return {
      date: firstStop.date,
      time: firstStop.time,
      city: firstStop.city,
      state: firstStop.state,
      address: firstStop.address
    };
  }
  
  const firstPickup = pickupStops.sort((a, b) => a.sequence - b.sequence)[0];
  return {
    date: firstPickup.date,
    time: firstPickup.time,
    city: firstPickup.city,
    state: firstPickup.state,
    address: firstPickup.address
  };
};

const getLastDelivery = (load) => {
  if (!load.stops || load.stops.length === 0) {
    // Fallback to legacy delivery data
    return {
      date: load.delivery_date,
      time: load.delivery_time,
      city: load.destination_city,
      state: load.destination_state,
      address: load.destination_address
    };
  }
  
  // Find last delivery stop (highest sequence with type 'delivery')
  const deliveryStops = load.stops.filter(stop => stop.stop_type === 'delivery');
  if (deliveryStops.length === 0) {
    // If no delivery stops found, use last stop
    const lastStop = load.stops.sort((a, b) => b.sequence - a.sequence)[0];
    return {
      date: lastStop.date,
      time: lastStop.time,
      city: lastStop.city,
      state: lastStop.state,
      address: lastStop.address
    };
  }
  
  const lastDelivery = deliveryStops.sort((a, b) => b.sequence - a.sequence)[0];
  return {
    date: lastDelivery.date,
    time: lastDelivery.time,
    city: lastDelivery.city,
    state: lastDelivery.state,
    address: lastDelivery.address
  };
};

const STATUS_CONFIG = {
  open: {
    color: 'yellow',
    label: 'Open for Bids',
    icon: Package,
    action: null,
    next: null
  },
  expired: {
    color: 'orange',
    label: 'Expired',
    icon: AlertCircle,
    action: null,
    next: null
  },
  assigned: {
    color: 'blue',
    label: 'Awaiting Driver',
    icon: Truck,
    action: 'Assign Driver',
    next: 'assigned_driver'
  },
  assigned_driver: {
    color: 'indigo',
    label: 'Driver Assigned',
    icon: MapPin,
    action: 'Start Trip',
    next: 'trip_started'
  },
  trip_started: {
    color: 'cyan',
    label: 'Trip Started',
    icon: Navigation,
    action: 'Arrive at First Stop',
    next: 'at_stop_0'
  },
  cancelled: {
    color: 'red',
    label: 'Cancelled',
    icon: X,
    action: null,
    next: null
  },
  completed: {
    color: 'green',
    label: 'Completed',
    icon: CheckCircle,
    action: null,
    next: null
  },
  getConfig: (status, load) => {
    if (!status) {
      return {
        color: 'gray',
        label: 'Status Unavailable',
        icon: AlertCircle,
        action: 'Update Status',
        next: null
      };
    }

    const cleanStatus = String(status).trim().toLowerCase();

    // First check exact matches including cancelled and completed
    if (STATUS_CONFIG[cleanStatus]) {
      return STATUS_CONFIG[cleanStatus];
    }

    // Handle stop-based statuses
    if (cleanStatus.startsWith('at_stop_')) {
      const stopNumber = parseInt(cleanStatus.split('_')[2]);
      const isLastStop = load?.stops && stopNumber === load.stops.length - 1;
      
      return {
        color: 'purple',
        label: `At Stop ${stopNumber + 1}${isLastStop ? ' (Final Stop)' : ''}`,
        icon: Navigation,
        action: isLastStop ? 'Complete Delivery' : 'Start Next Transit',
        next: isLastStop ? 'completed' : `in_transit_${stopNumber + 1}`
      };
    }
    
    if (cleanStatus.startsWith('in_transit_')) {
      const stopNumber = parseInt(cleanStatus.split('_')[2]);
      return {
        color: 'orange',
        label: `In Transit to Stop ${stopNumber + 1}`,
        icon: Flag,
        action: 'Mark At Next Stop',
        next: `at_stop_${stopNumber}`
      };
    }

    return {
      color: 'gray',
      label: 'Status Unavailable',
      icon: AlertCircle,
      action: 'Update Status',
      next: null
    };
  }
};

// Add StatusBadge component
const StatusBadge = ({ status, load }) => {
  // Always call hooks at the top level
  const { isMobile } = useScreenSize();
  
  // Use centralized isLoadExpired utility from loadUtils.js
  // This properly parses the ISO8601 pickup_datetime for accurate timezone handling
  const expired = isLoadExpired(load);

  // Check if load was resubmitted - prioritize this over expired status
  if (load?.resubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`
          inline-flex items-center
          ${isMobile ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}
          rounded-full 
          bg-gradient-to-r from-purple-500 to-purple-600
          shadow-lg
          backdrop-blur-sm
          border border-white/10
        `}
      >
        <RotateCcw className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-white mr-2`} />
        <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-white`}>
          Resubmitted
        </span>
      </motion.div>
    );
  }

  // Add better null safety for status
  const safeStatus = expired ? 'expired' : (status || 'unknown');
  const config = STATUS_CONFIG.getConfig(safeStatus.toLowerCase(), load || {});
  const Icon = config.icon;
  
  const colorVariants = {
    blue: 'bg-gradient-to-r from-blue-500 to-blue-600',
    indigo: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
    cyan: 'bg-gradient-to-r from-cyan-500 to-blue-600',
    purple: 'bg-gradient-to-r from-purple-500 to-purple-600',
    orange: 'bg-gradient-to-r from-orange-500 to-orange-600',
    green: 'bg-gradient-to-r from-green-500 to-green-600',
    yellow: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    red: 'bg-gradient-to-r from-red-500 to-red-600',
    gray: 'bg-gradient-to-r from-gray-500 to-gray-600'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`
        inline-flex items-center
        ${isMobile ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}
        rounded-full 
        ${colorVariants[config.color]}
        shadow-lg
        backdrop-blur-sm
        border border-white/10
      `}
    >
      <Icon className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-white mr-2`} />
      <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-white`}>
        {config.label}
      </span>
    </motion.div>
  );
};

const LoadPage = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);
  
  // Add loading state from Redux store
  const loadingState = useSelector(state => 
    userType === 'carrier' || userType === 'carrier_admin'
      ? state.loadCarrier.status
      : state.load.status
  );

  const load = useSelector(state => 
    userType === 'carrier' || userType === 'carrier_admin'
      ? selectCarrierLoadById(state, loadId)
      : selectLoadById(state, loadId)
  );

  const [error, setError] = useState(null);
  const { isMobile, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  useEffect(() => {
    const fetchLoadDetails = async () => {
      if (!loadId) {
        setError({ type: 'error', message: 'Invalid load ID' });
        return;
      }

      try {
        // Fetch load details
        await dispatch(
          userType === 'carrier' || userType === 'carrier_admin'
            ? fetchSingleCarrierLoad(loadId)
            : fetchSingleLoad(loadId)
        ).unwrap();

        // Also fetch tracking history
        await dispatch(fetchTrackingHistory({ 
          loadId,
          page: 1,
          perPage: 100
        })).unwrap();
      } catch (err) {
        // Enhanced error handling
        if (err.status === 404) {
          setError({
            type: 'not_found',
            message: err.data?.message || 'Load not found'
          });
        } else {
          setError(handleError(err));
        }
      }
    };

    fetchLoadDetails();
  }, [loadId, userType, dispatch]);

  // Add effect to watch for load updates and clear any existing errors
  useEffect(() => {
    if (load && error) {
      setError(null);
    }
  }, [load, error]);

  // Add sidebar responsiveness
  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  // Add early return for loading state
  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Add early return for error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`max-w-md w-full bg-white rounded-xl shadow-lg ${
            isMobile ? 'p-4 mx-4' : 'p-6'
          } text-center`}
        >
          {error.type === 'unauthorized' ? (
            <>
              <Lock className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-6">
                You don't have permission to view this load.
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Return Home
              </button>
            </>
          ) : error.type === 'unauthenticated' ? (
            <>
              <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Required</h2>
              <p className="text-gray-600 mb-6">
                Please log in to view this load.
              </p>
              <div className="space-x-4">
                <button
                  onClick={() => navigate('/login', { 
                    state: { returnUrl: `/loads/${loadId}` }
                  })}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Log In
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Return Home
                </button>
              </div>
            </>
          ) : error.type === 'not_found' ? (
            <>
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Load Not Found</h2>
              <p className="text-gray-600 mb-6">
                {error.message || 'The requested load could not be found.'}
              </p>
              <div className="space-x-4">
                <button
                  onClick={() => navigate(
                    userType === 'carrier' || userType === 'carrier_admin' 
                      ? '/carrier/loads' 
                      : '/customer/shipments'
                  )}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  View All Loads
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Return Home
                </button>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
              <p className="text-gray-600 mb-6">{error.message}</p>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Return Home
              </button>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // Add early return for missing load data
  if (!load && loadingState === 'succeeded') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center"
        >
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Load Not Found</h2>
          <p className="text-gray-600 mb-6">
            The requested load could not be found.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate(
                userType === 'carrier' || userType === 'carrier_admin' 
                  ? '/carrier/loads' 
                  : '/customer/shipments'
              )}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              View All Loads
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Return Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Guard clause to prevent rendering with missing load data
  if (!load) return null;

  // Main content render - now we're sure load exists
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <SideNavigation 
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onLogout={handleLogout}
      />
      
      <div className={`flex-1 transition-all duration-300 ${
        isMobile ? 'ml-0 px-2' : (sidebarOpen ? 'ml-64 px-6' : 'ml-16 px-6')
      }`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-8">
          {/* Enhanced Header Section with StatusBadge for both mobile and desktop */}
          <div className={`${isMobile ? 'mb-4' : 'mb-8'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4">
                {isMobile && (
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50"
                  >
                    <Menu className="h-5 w-5 text-gray-600" />
                  </button>
                )}
                <h1 className={`font-bold text-gray-900 ${isMobile ? 'text-xl' : 'text-2xl'}`}>
                  Load {getLoadDisplayId(load)}
                </h1>
              </div>
              <StatusBadge status={load.status} load={load} />
            </div>
            {!isMobile && (
              <p className="text-gray-500">
                {load.origin_city}, {load.origin_state} to {load.destination_city}, {load.destination_state}
              </p>
            )}
          </div>

          {/* Refined Grid Layout */}
          <div className={`grid ${
            isMobile 
              ? 'grid-cols-1 gap-4' 
              : 'grid-cols-1 lg:grid-cols-3 gap-8'
          }`}>
            {/* Left Column - Map and Timeline */}
            <div className="lg:col-span-2 space-y-6">
              {/* Map Card */}
              <Card noPadding className="overflow-hidden" style={{ height: isMobile ? '300px' : '450px' }}>
                <LoadTrackingMap 
                  load={load}
                  telematics={load?.trackingData || null}
                />
              </Card>

              {/* Timeline Card */}
              <Card>
                <SectionHeader icon={Clock} title="Status Timeline" />
                <LoadStatusTimeline 
                  loadId={load.id}
                  isCarrier={userType === 'carrier' || userType === 'carrier_admin'}
                  statusHistory={load.status_history}
                />
              </Card>
            </div>

            {/* Right Column - Load Details */}
            <div className="space-y-6">
              {/* Load Information Card */}
              <Card>
                <SectionHeader icon={Package} title="Load Information" />
                <div className={`space-y-6`}>
                  
                  {/* Driver Information Section */}
                  {load.assigned_driver && (
                    <div className="pb-4 border-b border-gray-100">
                      <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                        <User className="w-4 h-4 mr-2 text-blue-500" />
                        Assigned Driver
                      </h3>
                      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                        <DetailItem 
                          icon={User} 
                          label="Name" 
                          value={load.assigned_driver.name} 
                        />
                        {load.assigned_driver.phone && (
                          <DetailItem 
                            icon={Phone} 
                            label="Phone" 
                            value={load.assigned_driver.phone} 
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Vehicle & Trailer Information Section */}
                  {(load.vehicle_info || load.trailer_info) && (
                    <div className="pb-6 border-b border-gray-100">
                      <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-4 flex items-center`}>
                        <Truck className="w-4 h-4 mr-2 text-blue-500" />
                        Assigned Vehicle & Trailer
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Vehicle Section */}
                        {load.vehicle_info && (
                          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/50 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm font-semibold text-blue-900 flex items-center">
                                <Truck className="w-4 h-4 mr-2" />
                                Vehicle Information
                              </h4>
                            </div>
                            
                            <div className="space-y-3">
                              {load.vehicle_info.type && (
                                <VehicleDetailItem 
                                  icon={Truck} 
                                  label="Vehicle Type" 
                                  value={load.vehicle_info.type} 
                                />
                              )}
                              
                              {load.vehicle_info.make && load.vehicle_info.model && (
                                <VehicleDetailItem 
                                  icon={Car} 
                                  label="Make & Model" 
                                  value={`${load.vehicle_info.make} ${load.vehicle_info.model}`} 
                                />
                              )}
                              
                              <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-3'}`}>
                                {load.vehicle_info.year && (
                                  <VehicleDetailItem 
                                    icon={Calendar} 
                                    label="Year" 
                                    value={load.vehicle_info.year.toString()} 
                                  />
                                )}
                                
                                {load.vehicle_info.tractor_number && (
                                  <VehicleDetailItem 
                                    icon={Hash} 
                                    label="Tractor #" 
                                    value={load.vehicle_info.tractor_number} 
                                  />
                                )}
                              </div>
                              
                              {load.vehicle_info.plate_number && (
                                <VehicleDetailItem 
                                  icon={Hash} 
                                  label="License Plate" 
                                  value={load.vehicle_info.plate_number} 
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Trailer Section */}
                        {load.trailer_info ? (
                          <div className="p-4 bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl border border-green-200/50 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm font-semibold text-green-900 flex items-center">
                                <Package className="w-4 h-4 mr-2" />
                                Trailer Information
                              </h4>
                              {load.trailer_info.is_primary && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full border border-yellow-200">
                                  PRIMARY
                                </span>
                              )}
                            </div>
                            
                            <div className="space-y-3">
                              <VehicleDetailItem 
                                icon={Hash} 
                                label="Trailer #" 
                                value={load.trailer_info.trailer_number} 
                              />
                              
                              {load.trailer_info.type && (
                                <VehicleDetailItem 
                                  icon={Package} 
                                  label="Trailer Type" 
                                  value={load.trailer_info.type} 
                                />
                              )}
                              
                              {load.trailer_info.make && load.trailer_info.model && (
                                <VehicleDetailItem 
                                  icon={Car} 
                                  label="Make & Model" 
                                  value={`${load.trailer_info.make} ${load.trailer_info.model}`} 
                                />
                              )}
                              
                              <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-3'}`}>
                                {load.trailer_info.year && (
                                  <VehicleDetailItem 
                                    icon={Calendar} 
                                    label="Year" 
                                    value={load.trailer_info.year.toString()} 
                                  />
                                )}
                                
                                {load.trailer_info.capacity && (
                                  <VehicleDetailItem 
                                    icon={Weight} 
                                    label="Capacity" 
                                    value={`${load.trailer_info.capacity} tons`} 
                                  />
                                )}
                                
                                {load.trailer_info.length && (
                                  <VehicleDetailItem 
                                    icon={Ruler} 
                                    label="Length" 
                                    value={`${load.trailer_info.length} ft`} 
                                  />
                                )}
                              </div>
                              
                              {load.trailer_info.license_plate && (
                                <VehicleDetailItem 
                                  icon={Hash} 
                                  label="License Plate" 
                                  value={load.trailer_info.license_plate} 
                                />
                              )}
                            </div>
                          </div>
                        ) : load.vehicle_info && (
                          <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-100/50 rounded-xl border border-amber-200/50 shadow-sm">
                            <div className="flex items-center text-amber-700">
                              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="text-sm font-medium">No trailer assigned to this load</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Carrier Information Section */}
                  {load.assigned_carrier && (
                    <div className="pb-4 border-b border-gray-100">
                      <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                        <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                        Carrier
                      </h3>
                      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                        <DetailItem 
                          icon={Building2} 
                          label="Company" 
                          value={load.assigned_carrier.company_name} 
                        />
                        
                      </div>
                    </div>
                  )}

                  {/* Customer Information Section */}
                  {load.customer && (
                    <div className="pb-4 border-b border-gray-100">
                      <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                        <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                        Customer
                      </h3>
                      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                        <DetailItem 
                          icon={Building2} 
                          label="Company" 
                          value={load.customer.company_name} 
                        />
                        
                      </div>
                    </div>
                  )}

                  {/* Load Details Section */}
                  <div className="pb-4 border-b border-gray-100">
                    <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                      <Package className="w-4 h-4 mr-2 text-blue-500" />
                      Load Details
                    </h3>
                    <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                      {load.load_type && (
                        <DetailItem 
                          icon={Package} 
                          label="Load Type" 
                          value={load.load_type} 
                        />
                      )}
                      {load.weight && (
                        <DetailItem 
                          icon={Weight} 
                          label="Weight" 
                          value={`${load.weight} lbs`} 
                        />
                      )}
                      {load.pieces && (
                        <DetailItem 
                          icon={Package} 
                          label="Pieces" 
                          value={load.pieces} 
                        />
                      )}
                      {load.dimensions && (
                        <DetailItem 
                          icon={Ruler} 
                          label="Dimensions" 
                          value={`${load.dimensions.length}″ × ${load.dimensions.width}″ × ${load.dimensions.height}″`} 
                        />
                      )}
                      {load.description && (
                        <DetailItem 
                          icon={FileText} 
                          label="Description" 
                          value={load.description} 
                        />
                      )}
                      {load.special_requirements && (
                        <DetailItem 
                          icon={Info} 
                          label="Special Requirements" 
                          value={load.special_requirements} 
                        />
                      )}
                    </div>
                  </div>

                  

                  {/* Timeline Information Section */}
                  <div className="pb-4 border-b border-gray-100">
                    <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                      <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                      Timeline
                    </h3>
                    <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                      {(() => {
                        const firstPickup = getFirstPickup(load);
                        const lastDelivery = getLastDelivery(load);
                        
                        return (
                          <>
                            {firstPickup.date && (
                              <DetailItem 
                                icon={Calendar} 
                                label="First Pickup Date" 
                                value={safeFormatDate(firstPickup.date) || 'N/A'} 
                              />
                            )}
                            {lastDelivery.date && (
                              <DetailItem 
                                icon={Calendar} 
                                label="Final Delivery Date" 
                                value={safeFormatDate(lastDelivery.date) || 'N/A'} 
                              />
                            )}
                          </>
                        );
                      })()}
                      {load.trip_started_at && (
                        <DetailItem 
                          icon={Clock} 
                          label="Trip Started" 
                          value={new Date(load.trip_started_at).toLocaleString()} 
                        />
                      )}
                      {load.created_at && (
                        <DetailItem 
                          icon={Calendar} 
                          label="Created" 
                          value={new Date(load.created_at).toLocaleDateString()} 
                        />
                      )}
                    </div>
                  </div>

                  {/* Route Information Section */}
                  <div>
                    <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-800 mb-3 flex items-center`}>
                      <Route className="w-4 h-4 mr-2 text-blue-500" />
                      Route Information
                    </h3>
                    <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-4'}`}>
                      {load.origin_city && load.origin_state && (
                        <DetailItem 
                          icon={MapPin} 
                          label="Origin" 
                          value={`${load.origin_city}, ${load.origin_state}`} 
                        />
                      )}
                      {load.destination_city && load.destination_state && (
                        <DetailItem 
                          icon={Flag} 
                          label="Destination" 
                          value={`${load.destination_city}, ${load.destination_state}`} 
                        />
                      )}
                      {load.stops && load.stops.length > 0 && (
                        <DetailItem 
                          icon={Navigation} 
                          label="Total Stops" 
                          value={`${load.stops.length} stops`} 
                        />
                      )}
                      {load.origin_address && (
                        <DetailItem 
                          icon={MapPin} 
                          label="Origin Address" 
                          value={load.origin_address} 
                        />
                      )}
                      {load.destination_address && (
                        <DetailItem 
                          icon={Flag} 
                          label="Destination Address" 
                          value={load.destination_address} 
                        />
                      )}
                    </div>
                  </div>

                </div>
              </Card>

              {/* Documents Section */}
              <Card>
                <SectionHeader icon={FileText} title="Documents" />
                <div className="space-y-3">
                  {load.documents?.map((doc) => (
                    <DocumentCard 
                      key={doc.id} 
                      document={doc}
                      className={isMobile ? '' : 'hover:shadow-md transition-shadow duration-300'}
                    />
                  ))}
                  {(!load.documents || load.documents.length === 0) && (
                    <p className="text-sm text-gray-500 italic">No documents available</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to handle errors
const handleError = (err) => {
  if (err.status === 401) {
    return {
      type: 'unauthenticated',
      message: 'Please log in to view this load'
    };
  } else if (err.status === 403) {
    return {
      type: 'unauthorized',
      message: 'You do not have permission to view this load'
    };
  } else if (err.status === 404) {
    return {
      type: 'not_found',
      message: err.data?.message || 'Load not found'
    };
  }
  return {
    type: 'error',
    message: err.message || 'Failed to fetch load details'
  };
};

export default LoadPage; 